import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, AlertTriangle, ArrowRight, Zap } from 'lucide-react';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import {
  RecommendationCard,
  ChannelBadge,
  RecommendationHeader,
  RecommendationContent,
  OptimizeButton,
  BandLabelTypography,
  StatusTypography,
  ChannelBadgeTypography,
} from './styles';

interface RecommendationSectionProps {
  band: BandResult;
  bandLabel: string;
  onOptimize?: () => void;
  optimizing?: boolean;
}

/** Displays the current and recommended channel for a band */
export function RecommendationSection({
  band,
  bandLabel,
  onOptimize,
  optimizing,
}: RecommendationSectionProps) {
  const theme = useTheme();
  const { t } = useTranslation();

  const isOptimized =
    !band.recommendedChannel || band.currentChannel === band.recommendedChannel;

  return (
    <RecommendationCard optimized={isOptimized}>
      <RecommendationHeader>
        <Stack direction="row" alignItems="center" gap={1.5}>
          {isOptimized ? (
            <CheckCircle2 size={24} color={theme.palette.status.online} />
          ) : (
            <AlertTriangle size={24} color={theme.palette.status.warning} />
          )}
          <Stack>
            <BandLabelTypography variant="body2">
              {bandLabel}
            </BandLabelTypography>
            <StatusTypography variant="caption" optimized={isOptimized}>
              {isOptimized ? t('scanner.optimized') : t('scanner.improvement')}
            </StatusTypography>
          </Stack>
        </Stack>
      </RecommendationHeader>

      <RecommendationContent>
        <ChannelBadge>
          <ChannelBadgeTypography variant="caption">
            {t('scanner.currentChannel')}: {band.currentChannel}
          </ChannelBadgeTypography>
        </ChannelBadge>

        <ArrowRight size={16} color={theme.palette.white['06']} />

        <ChannelBadge highlight={!isOptimized}>
          <ChannelBadgeTypography variant="caption">
            {t('scanner.recommendedChannel')}: {band.recommendedChannel}
          </ChannelBadgeTypography>
        </ChannelBadge>

        {!isOptimized && onOptimize && (
          <OptimizeButton
            onClick={onOptimize}
            disabled={optimizing}
            type="button"
          >
            {optimizing ? (
              <>
                <CircularProgress size={12} color="inherit" />
                <span>{t('scanner.optimizingButton')}</span>
              </>
            ) : (
              <>
                <Zap size={13} />
                <span>{t('scanner.optimizeButton')}</span>
              </>
            )}
          </OptimizeButton>
        )}
      </RecommendationContent>
    </RecommendationCard>
  );
}
